import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Send, ChevronLeft, Search, CheckCircle2, RotateCcw, Clock, Zap, UserCheck, Settings2, Trash2, Edit2, Plus } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import useIsMobile from '../../utils/useIsMobile';
import { avvisaTicketCambiati } from '../../utils/useTicketDaLeggere';
import { ARGOMENTI_TICKET, etichettaArgomento } from '../../utils/ticketArgomenti';
import { SceltaAllegati, ElencoAllegati } from '../../components/supporto/AllegatiTicket';
import InFinestra from '../../components/InFinestra';

// Supporto lato staff: tutte le richieste di utenti e allenatori, conversazione e risposta.
// Tutto dentro il gestionale, nessuna email (decisione titolare 08/10). Permesso "ticket":
// vedi = leggere, modifica = rispondere e cambiare stato.
// 21/10: argomento (si vedono solo quelli dei propri moduli), presa in carico, allegati, risposte pronte,
// chiusura automatica dopo 7 giorni senza risposta dell'utente.
const STATO = {
  APERTO:         { label: 'Aperta',         col: '#B45309', bg: '#FEF3C7' },
  IN_LAVORAZIONE: { label: 'In lavorazione', col: '#1D4ED8', bg: '#DBEAFE' },
  CHIUSO:         { label: 'Chiusa',         col: '#15803D', bg: '#DCFCE7' },
};
const FILTRI = [
  { id: 'DA_RISPONDERE', label: 'Da rispondere' },
  { id: 'APERTO', label: 'Aperte' },
  { id: 'IN_LAVORAZIONE', label: 'In lavorazione' },
  { id: 'CHIUSO', label: 'Chiuse' },
  { id: '', label: 'Tutte' },
];
const quando = (d) => {
  const dt = new Date(d), ora = new Date();
  const stessoGiorno = dt.toDateString() === ora.toDateString();
  return stessoGiorno ? dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
};
const completo = (d) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const Badge = ({ stato }) => <span style={{ ...s.badge, color: STATO[stato]?.col, background: STATO[stato]?.bg }}>{STATO[stato]?.label || stato}</span>;

export default function SupportoStaff() {
  const { user } = useAuth();
  const puoRispondere = puoFare(user, 'ticket', 'edit');
  const mobile = useIsMobile();
  const [filtro, setFiltro] = useState('DA_RISPONDERE');
  const [q, setQ] = useState('');
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aperta, setAperta] = useState(null);
  const [testo, setTesto] = useState('');
  const [invio, setInvio] = useState(false);
  const [err, setErr] = useState('');
  const [argomento, setArgomento] = useState('');
  const [assegnate, setAssegnate] = useState('');   // '' | 'mie' | 'libere'
  const [files, setFiles] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [risposte, setRisposte] = useState([]);
  const [menuRisposte, setMenuRisposte] = useState(false);
  const [gestisciRisposte, setGestisciRisposte] = useState(false);
  const fondo = useRef(null);
  const area = useRef(null);

  const caricaRisposte = () => puoRispondere && api.get('/tickets/risposte-pronte').then(r => setRisposte(r.data)).catch(() => {});
  useEffect(() => { caricaRisposte(); }, []); // eslint-disable-line

  const carica = async () => {
    try {
      const params = {};
      if (filtro && filtro !== 'DA_RISPONDERE') params.stato = filtro;
      if (q.trim()) params.q = q.trim();
      if (argomento) params.argomento = argomento;
      if (assegnate) params.assegnate = assegnate;
      const r = await api.get('/tickets', { params });
      setLista(filtro === 'DA_RISPONDERE' ? r.data.filter(t => t.da_leggere) : r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Impossibile caricare le richieste'); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(carica, q ? 300 : 0); return () => clearTimeout(t); }, [filtro, q, argomento, assegnate]); // eslint-disable-line
  // l'elenco si aggiorna da solo quando arriva qualcosa (avviso a schermo / contatore)
  useEffect(() => { const f = () => carica(); window.addEventListener('ticket-cambiati', f); return () => window.removeEventListener('ticket-cambiati', f); }); // eslint-disable-line

  const apri = async (id) => {
    setErr('');
    try {
      const r = await api.get(`/tickets/${id}`);
      setAperta(r.data); setTesto(''); setFiles([]); setMenuRisposte(false);
      if (puoRispondere) api.get(`/tickets/${id}/operatori`).then(o => setOperatori(o.data)).catch(() => setOperatori([]));
      setTimeout(() => fondo.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 80);
    } catch (e) { setErr(e.response?.data?.error || 'Richiesta non trovata'); }
  };

  const rispondi = async () => {
    if ((!testo.trim() && !files.length) || !aperta) return;
    setInvio(true); setErr('');
    try {
      const r = await api.post(`/tickets/${aperta.id}/reply`, { testo, allegati: files.map(f => ({ nome: f.nome, dati: f.dati })) });
      setAperta(r.data); setTesto(''); setFiles([]); carica(); avvisaTicketCambiati();
      setTimeout(() => fondo.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }), 80);
    } catch (e) { setErr(e.response?.data?.error || 'Invio non riuscito'); }
    finally { setInvio(false); }
  };

  const cambiaStato = async (stato) => {
    if (stato === 'CHIUSO' && !window.confirm('Chiudere la richiesta? L\'utente non potrà più scrivere qui e dovrà aprirne una nuova.')) return;
    try { const r = await api.patch(`/tickets/${aperta.id}/stato`, { stato }); setAperta(r.data); carica(); avvisaTicketCambiati(); }
    catch (e) { setErr(e.response?.data?.error || 'Errore'); }
  };

  const assegna = async (user_id) => {
    setErr('');
    try { const r = await api.patch(`/tickets/${aperta.id}/assegna`, { user_id }); setAperta(r.data); carica(); avvisaTicketCambiati(); }
    catch (e) { setErr(e.response?.data?.error || 'Errore'); }
  };
  const cambiaArgomento = async (arg) => {
    setErr('');
    try {
      const r = await api.patch(`/tickets/${aperta.id}/argomento`, { argomento: arg });
      if (r.data.spostata) { setAperta(null); alert('Richiesta spostata in un argomento che non segui: ora la vedono i colleghi di quell\'argomento.'); }
      else { setAperta(r.data); api.get(`/tickets/${aperta.id}/operatori`).then(o => setOperatori(o.data)).catch(() => {}); }
      carica(); avvisaTicketCambiati();
    } catch (e) { setErr(e.response?.data?.error || 'Errore'); }
  };
  const usaRisposta = (r) => {
    const nome = (aperta?.user?.nome || '').trim().split(/\s+/)[0] || '';
    const t = r.testo.replace(/\{nome\}/g, nome).replace(/Ciao ,/g, 'Ciao,');
    setTesto(x => (x.trim() ? x.replace(/\s*$/, '\n\n') : '') + t);
    setMenuRisposte(false);
    setTimeout(() => area.current?.focus(), 30);
  };
  const risposteArg = risposte.filter(r => !r.argomento || r.argomento === aperta?.argomento);

  const elenco = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
      <div style={s.cerca}>
        <Search size={16} color="var(--text-dim)" />
        <input style={s.cercaInput} placeholder="Cerca per oggetto, nome o email…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <select style={{ ...s.pill, paddingRight: '28px', ...(argomento ? s.selOn : {}) }} value={argomento} onChange={e => setArgomento(e.target.value)}>
          <option value="">Tutti gli argomenti</option>
          {ARGOMENTI_TICKET.map(a => <option key={a.chiave} value={a.chiave}>{a.label}</option>)}
        </select>
        {[['', 'Tutte'], ['mie', 'Le mie'], ['libere', 'Da prendere in carico']].map(([v, l]) => (
          <button key={v} onClick={() => setAssegnate(v)} style={{ ...s.pill, ...(assegnate === v ? s.pillOn : {}) }}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {FILTRI.map(f => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{ ...s.pill, ...(filtro === f.id ? s.pillOn : {}) }}>{f.label}</button>
        ))}
      </div>
      {loading ? <div style={s.vuoto}>Caricamento…</div> : lista.length === 0 ? (
        <div style={s.vuoto}>
          <MessageSquare size={34} color="var(--text-dim)" />
          <div style={{ fontWeight: 700, marginTop: '10px' }}>{filtro === 'DA_RISPONDERE' ? 'Nessuna richiesta in attesa di risposta' : 'Nessuna richiesta'}</div>
        </div>
      ) : lista.map(t => (
        <div key={t.id} onClick={() => apri(t.id)} style={{ ...s.riga, ...(aperta?.id === t.id ? s.rigaOn : {}) }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {t.da_leggere && <span style={s.pallino} title="In attesa di risposta" />}
            <span style={{ fontWeight: 700, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.oggetto}</span>
            <span style={{ fontSize: '12px', color: 'var(--text-dim)', flexShrink: 0 }}>{quando(t.ultimo_msg_at)}</span>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '3px' }}>{t.user?.nome} · {t.user?.ruolo === 'ALLENATORE' ? 'allenatore' : 'utente'}</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
            <span style={s.arg}>{t.argomento_label}</span>
            {t.assegnato_a ? <span style={{ ...s.arg, ...(t.mia ? s.argMia : {}) }}>{t.mia ? 'Mia' : t.assegnato_a.nome}</span> : t.stato !== 'CHIUSO' && <span style={{ ...s.arg, color: '#B45309', background: '#FEF3C7' }}>Da prendere in carico</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-dim)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.anteprima}</span>
            <Badge stato={t.stato} />
          </div>
        </div>
      ))}
    </div>
  );

  const conversazione = aperta && (
    <div style={s.chat}>
      <div style={s.chatTesta}>
        {mobile && <button style={s.indietro} onClick={() => setAperta(null)}><ChevronLeft size={16} /> Tutte</button>}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '18px' }}>{aperta.oggetto}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '3px' }}>
              {aperta.user?.nome} · {aperta.user?.email} · #{aperta.id} del {completo(aperta.created_at)}
            </div>
          </div>
          <Badge stato={aperta.stato} />
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '12px', fontSize: '13px' }}>
          <span style={{ color: 'var(--text-dim)' }}>Argomento</span>
          {puoRispondere ? (
            <select style={s.selPic} value={aperta.argomento} onChange={e => cambiaArgomento(e.target.value)} title="Se l'utente ha sbagliato argomento, spostala">
              {ARGOMENTI_TICKET.map(a => <option key={a.chiave} value={a.chiave}>{a.label}</option>)}
            </select>
          ) : <b>{etichettaArgomento(aperta.argomento)}</b>}
          <span style={{ color: 'var(--text-dim)', marginLeft: '6px' }}>Se ne occupa</span>
          {puoRispondere ? (
            <select style={s.selPic} value={aperta.assegnato_a?.id || ''} onChange={e => assegna(e.target.value ? +e.target.value : null)}>
              <option value="">— nessuno —</option>
              {aperta.assegnato_a && !operatori.some(o => o.id === aperta.assegnato_a.id) && <option value={aperta.assegnato_a.id}>{aperta.assegnato_a.nome}</option>}
              {operatori.map(o => <option key={o.id} value={o.id}>{o.id === user?.id ? `${o.nome} (io)` : o.nome}</option>)}
            </select>
          ) : <b>{aperta.assegnato_a?.nome || 'nessuno'}</b>}
          {puoRispondere && !aperta.mia && aperta.stato !== 'CHIUSO' && <button style={s.btnSec} onClick={() => assegna(user.id)}><UserCheck size={14} /> Me ne occupo io</button>}
        </div>
        {puoRispondere && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
            {aperta.stato !== 'IN_LAVORAZIONE' && aperta.stato !== 'CHIUSO' && <button style={s.btnSec} onClick={() => cambiaStato('IN_LAVORAZIONE')}><Clock size={14} /> In lavorazione</button>}
            {aperta.stato !== 'CHIUSO' && <button style={s.btnSec} onClick={() => cambiaStato('CHIUSO')}><CheckCircle2 size={14} /> Chiudi</button>}
            {aperta.stato === 'CHIUSO' && <button style={s.btnSec} onClick={() => cambiaStato('IN_LAVORAZIONE')}><RotateCcw size={14} /> Riapri</button>}
          </div>
        )}
        {aperta.stato === 'CHIUSO' && aperta.chiusa_auto && <div style={{ fontSize: '12.5px', color: 'var(--text-dim)', marginTop: '8px' }}>Chiusa da sola: 7 giorni senza risposta dell'utente. Se riscrive si riapre.</div>}
      </div>

      <div style={s.messaggi}>
        {aperta.messaggi?.map(m => {
          const staff = m.user_id !== aperta.user_id;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: staff ? 'flex-end' : 'flex-start' }}>
              <div style={{ ...s.bolla, ...(staff ? s.bollaStaff : s.bollaUtente) }}>
                <div style={{ fontSize: '12px', fontWeight: 700, opacity: .8, marginBottom: '4px' }}>{staff ? (m.user?.nome || 'Staff') : (m.user?.nome || 'Utente')} · {completo(m.created_at)}</div>
                {m.testo && <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '15px', lineHeight: 1.45 }}>{m.testo}</div>}
                <ElencoAllegati allegati={m.allegati} chiaro={staff} />
              </div>
            </div>
          );
        })}
        <div ref={fondo} />
      </div>

      {err && <div style={s.err}>{err}</div>}
      {aperta.stato === 'CHIUSO' ? (
        <div style={s.chiusa}>Richiesta chiusa{puoRispondere ? ' — riaprila per rispondere.' : '.'}</div>
      ) : puoRispondere ? (
        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', position: 'relative' }}>
            <button type="button" style={s.btnSec} onClick={() => setMenuRisposte(v => !v)}><Zap size={14} /> Risposte pronte{risposteArg.length ? ` (${risposteArg.length})` : ''}</button>
            <button type="button" style={{ ...s.btnSec, padding: '7px 10px' }} onClick={() => setGestisciRisposte(true)} title="Gestisci le risposte pronte"><Settings2 size={14} /></button>
            <SceltaAllegati files={files} setFiles={setFiles} onErrore={setErr} />
            {menuRisposte && (
              <div style={s.menu}>
                {risposteArg.length === 0 ? <div style={{ ...s.vuoto, padding: '14px' }}>Nessuna risposta pronta per questo argomento. Creale con l'ingranaggio.</div> : risposteArg.map(r => (
                  <button key={r.id} type="button" style={s.voceMenu} onClick={() => usaRisposta(r)}>
                    <b>{r.titolo}</b>
                    <span style={{ fontSize: '12px', color: 'var(--text-dim)' }}>{r.argomento ? etichettaArgomento(r.argomento) : 'Tutti gli argomenti'} · {r.testo.slice(0, 90)}{r.testo.length > 90 ? '…' : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
            <textarea ref={area} style={s.area} placeholder="Scrivi la risposta… (Ctrl+Invio per inviare)" value={testo} maxLength={5000}
              onChange={e => setTesto(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) rispondi(); }} />
            <button style={{ ...s.invia, opacity: ((!testo.trim() && !files.length) || invio) ? .5 : 1 }} disabled={(!testo.trim() && !files.length) || invio} onClick={rispondi}><Send size={15} /> {invio ? 'Invio…' : 'Invia'}</button>
          </div>
        </div>
      ) : (
        <div style={s.chiusa}>Hai accesso in sola lettura.</div>
      )}
    </div>
  );

  return (
    <div>
      <h1 style={s.titolo}>SUPPORTO</h1>
      <p style={s.sub}>Le richieste che utenti e allenatori scrivono dal portale. Rispondi qui: la vedono nella loro pagina Supporto. Vedi solo gli argomenti legati ai moduli del tuo ruolo; chi risponde per primo se ne occupa. Se l'utente non risponde per 7 giorni la richiesta si chiude da sola.</p>
      {gestisciRisposte && <GestisciRisposte risposte={risposte} onChiudi={() => setGestisciRisposte(false)} onCambio={caricaRisposte} />}
      {mobile ? (aperta ? conversazione : elenco) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 380px) minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
          {elenco}
          {conversazione || <div style={{ ...s.chat, ...s.vuoto, minHeight: '320px', justifyContent: 'center' }}><MessageSquare size={34} color="var(--text-dim)" /><div style={{ marginTop: '10px' }}>Seleziona una richiesta per leggerla</div></div>}
        </div>
      )}
    </div>
  );
}

// Risposte pronte (21/10): testi per le domande ricorrenti, {nome} diventa il nome di chi ha scritto
function GestisciRisposte({ risposte, onChiudi, onCambio }) {
  const vuota = { id: null, titolo: '', argomento: '', testo: '' };
  const [f, setF] = useState(null);
  const [err, setErr] = useState('');
  const salva = async () => {
    setErr('');
    try {
      if (f.id) await api.put(`/tickets/risposte-pronte/${f.id}`, f); else await api.post('/tickets/risposte-pronte', f);
      setF(null); onCambio();
    } catch (e) { setErr(e.response?.data?.error || 'Errore'); }
  };
  const elimina = async (r) => {
    if (!window.confirm(`Eliminare la risposta "${r.titolo}"?`)) return;
    try { await api.delete(`/tickets/risposte-pronte/${r.id}`); onCambio(); } catch (e) { setErr(e.response?.data?.error || 'Errore'); }
  };
  return (
    <InFinestra attiva larghezza={640} onChiudi={onChiudi}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '16px', padding: '20px', color: 'var(--text)' }}>
        <h3 style={{ margin: '0 0 4px' }}>Risposte pronte</h3>
        <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '14px' }}>Si inseriscono nella risposta con un clic e si possono ritoccare prima di inviare. Scrivi <b>{'{nome}'}</b> dove vuoi il nome di chi ha scritto.</div>
        {err && <div style={{ ...s.err, padding: '0 0 10px' }}>{err}</div>}
        {f ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <input style={s.campo} placeholder="Titolo (es. Orari della segreteria)" maxLength={120} value={f.titolo} onChange={e => setF({ ...f, titolo: e.target.value })} />
            <select style={s.campo} value={f.argomento || ''} onChange={e => setF({ ...f, argomento: e.target.value })}>
              <option value="">Per tutti gli argomenti</option>
              {ARGOMENTI_TICKET.map(a => <option key={a.chiave} value={a.chiave}>Solo {a.label}</option>)}
            </select>
            <textarea style={{ ...s.campo, minHeight: '140px', resize: 'vertical' }} maxLength={5000} placeholder="Ciao {nome}, …" value={f.testo} onChange={e => setF({ ...f, testo: e.target.value })} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={s.btnSec} onClick={() => setF(null)}>Annulla</button>
              <button style={s.invia} onClick={salva}>Salva</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '55vh', overflowY: 'auto' }}>
              {risposte.length === 0 && <div style={s.vuoto}>Nessuna risposta pronta.</div>}
              {risposte.map(r => (
                <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '10px 12px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{r.titolo}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-dim)', margin: '2px 0 4px' }}>{r.argomento ? etichettaArgomento(r.argomento) : 'Tutti gli argomenti'}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>{r.testo}</div>
                  </div>
                  <button style={{ ...s.btnSec, padding: '6px 9px' }} onClick={() => setF({ id: r.id, titolo: r.titolo, argomento: r.argomento || '', testo: r.testo })} title="Modifica"><Edit2 size={14} /></button>
                  <button style={{ ...s.btnSec, padding: '6px 9px', color: '#B42318' }} onClick={() => elimina(r)} title="Elimina"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '14px' }}>
              <button style={s.invia} onClick={() => setF(vuota)}><Plus size={15} /> Nuova risposta</button>
              <button style={s.btnSec} onClick={onChiudi}>Chiudi</button>
            </div>
          </>
        )}
      </div>
    </InFinestra>
  );
}

const s = {
  arg: { fontSize: '11.5px', fontWeight: 700, color: 'var(--text-muted)', background: 'var(--bg-input)', borderRadius: '999px', padding: '2px 9px' },
  argMia: { color: '#fff', background: 'var(--accent-strong, #C24B08)' },
  selOn: { borderColor: '#F4600C', color: 'var(--text)', boxShadow: '0 0 0 1px #F4600C' },
  selPic: { border: '1px solid var(--border2)', borderRadius: '999px', padding: '5px 10px', fontSize: '13px', background: 'var(--bg-card)', color: 'var(--text)', width: 'auto' },
  menu: { position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, zIndex: 20, width: 'min(440px, 90vw)', maxHeight: '320px', overflowY: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '14px', boxShadow: '0 10px 30px rgba(0,0,0,.15)', padding: '6px' },
  voceMenu: { display: 'flex', flexDirection: 'column', gap: '2px', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '9px 10px', borderRadius: '10px', cursor: 'pointer', color: 'var(--text)', fontSize: '14px' },
  campo: { width: '100%', border: '1px solid var(--border2)', borderRadius: '12px', padding: '10px 12px', fontSize: '15px', color: 'var(--text)', background: 'var(--bg-card)', boxSizing: 'border-box' },
  titolo: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight: 800, letterSpacing: '2px', marginBottom: '6px' },
  sub: { color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: '20px' },
  cerca: { display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: '12px', padding: '0 12px' },
  cercaInput: { flex: 1, border: 'none', background: 'transparent', padding: '11px 0', outline: 'none', color: 'var(--text)', fontSize: '15px', boxShadow: 'none' },
  pill: { border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text-muted)', borderRadius: '999px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  pillOn: { background: 'var(--text)', color: 'var(--bg-card)', borderColor: 'var(--text)' },
  riga: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '14px', padding: '12px 14px', cursor: 'pointer' },
  rigaOn: { borderColor: 'var(--accent)', boxShadow: '0 0 0 3px rgba(244,96,12,.12)' },
  pallino: { width: '9px', height: '9px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 },
  badge: { fontSize: '12px', fontWeight: 700, borderRadius: '999px', padding: '3px 10px', whiteSpace: 'nowrap', flexShrink: 0 },
  vuoto: { display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--text-dim)', padding: '40px 0', textAlign: 'center' },
  chat: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' },
  chatTesta: { padding: '18px 20px', borderBottom: '1px solid var(--border)' },
  indietro: { display: 'flex', alignItems: 'center', gap: '4px', border: 'none', background: 'transparent', color: 'var(--text-muted)', fontWeight: 600, cursor: 'pointer', padding: '0 0 10px' },
  btnSec: { display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid var(--border2)', background: 'var(--bg-input)', color: 'var(--text)', borderRadius: '999px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  messaggi: { display: 'flex', flexDirection: 'column', gap: '10px', padding: '18px 20px', maxHeight: '58vh', overflowY: 'auto', background: 'var(--bg)' },
  bolla: { maxWidth: '82%', borderRadius: '18px', padding: '10px 14px' },
  bollaUtente: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderBottomLeftRadius: '6px' },
  bollaStaff: { background: 'var(--accent-strong, #C24B08)', color: '#fff', borderBottomRightRadius: '6px' },
  scrivi: { display: 'flex', gap: '10px', alignItems: 'flex-end', padding: '14px 16px', borderTop: '1px solid var(--border)' },
  area: { flex: 1, minHeight: '70px', resize: 'vertical', border: '1px solid var(--border2)', borderRadius: '12px', padding: '10px 12px', fontSize: '15px', color: 'var(--text)', outline: 'none' },
  invia: { display: 'flex', alignItems: 'center', gap: '6px', background: '#F4600C', color: '#fff', border: 'none', borderRadius: '999px', padding: '11px 18px', fontWeight: 700, cursor: 'pointer' },
  chiusa: { padding: '14px 20px', borderTop: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '14px', textAlign: 'center' },
  err: { color: '#B42318', fontSize: '14px', padding: '8px 20px 0' },
};
